// Payroll's first run (gap #12): open a pay period (payPeriods.create), set each person's pay and
// commission rates (payroll.setPay, commissions.setPlan; the owner's), lock a period's commissions
// and see them (commissions.lock, commissions.list), and record what the provider took from the bank
// (payroll.recordFunding). The server decides; a refusal is its words.

import { useState } from 'react';
import { client } from '../api.ts';
import { MoneySheet } from '../shared/MoneySheet.tsx';
import { useSaveKey } from '../shared/SaveButton.tsx';
import { dayText, parseMoney, todayIn } from './model.ts';
import { COMMISSION_RULES, commissionsByPerson, nextPeriod, percentToRate } from './payroll-model.ts';
import type { BankAccount, PayPeriod, PayPeriodSummary } from './types.ts';
import { scrollFocus } from '../shared/scrollFocus.ts';
import { Empty, errorText, Loading, Money, Panel, useAct, useQuery } from './ui.tsx';

interface Staff {
  id: string;
  displayName: string;
  isTechnician: boolean;
}

const cad = (cents: number) =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(cents / 100);

// ── open a pay period ──

export function OpenPeriod({
  newest,
  timeZone,
  toast,
  onOpened,
  quiet,
}: {
  newest: PayPeriodSummary | null;
  timeZone?: string;
  toast: (m: string) => void;
  onOpened: (id: string) => void;
  /** Beside an existing period: a small "Open the next period", not the empty state's panel. */
  quiet?: boolean;
}) {
  const week = useQuery<{ value: unknown }>('settings.get', { key: 'payroll.weekStartsOn' });
  const [open, setOpen] = useState(!quiet);
  const weekStartsOn = typeof week.data?.value === 'number' ? week.data.value : 0;
  const first = nextPeriod(newest, todayIn(timeZone), weekStartsOn);
  const [dates, setDates] = useState<typeof first | null>(null);
  const d = dates ?? first;
  const act = useAct(toast, () => undefined);
  const ok =
    !!d.startDate && !!d.endDate && !!d.payDate && d.startDate <= d.endDate && d.payDate >= d.endDate;
  if (!open)
    return (
      <button className="btn quiet" onClick={() => setOpen(true)}>
        Open the next period
      </button>
    );
  const field = (k: keyof typeof first, label: string) => (
    <label>
      <span className="flabel">{label}</span>
      <input
        className="field"
        type="date"
        value={d[k]}
        onChange={(e) => setDates({ ...d, [k]: e.target.value })}
      />
    </label>
  );
  return (
    <Panel title={newest ? 'The next pay period' : 'Open the first pay period'}>
      {!newest && (
        <p className="muted small">
          Hours come into a period from the technicians’ time entries; Prepare works them out at each person’s
          rate.
        </p>
      )}
      <form
        className="fgrid"
        aria-label="A pay period"
        onSubmit={(e) => {
          e.preventDefault();
          if (!ok || act.busy) return;
          void act
            .run<{ id: string }>(
              'payPeriods.create',
              d,
              `The period ${dayText(d.startDate)} to ${dayText(d.endDate)} is open.`,
            )
            .then((r) => {
              if (r) {
                setDates(null);
                if (quiet) setOpen(false);
                onOpened(r.id);
              }
            });
        }}
      >
        {field('startDate', 'From')}
        {field('endDate', 'To')}
        {field('payDate', 'Paid on')}
        <div className="row-actions">
          <button className="btn primary" type="submit" disabled={!ok || act.busy}>
            {act.busy ? 'Opening…' : 'Open the period'}
          </button>
          {quiet && (
            <button className="btn quiet" type="button" onClick={() => setOpen(false)} disabled={act.busy}>
              Cancel
            </button>
          )}
        </div>
      </form>
    </Panel>
  );
}

// ── each person's pay and commission rates (the owner's) ──

export function PayAndCommissions({ toast }: { toast: (m: string) => void }) {
  const staff = useQuery<{ staff: Staff[] }>('staff.list', {});
  const [staffId, setStaffId] = useState('');
  const person = staff.data?.staff.find((s) => s.id === staffId);
  return (
    <Panel title="Pay and commission rates">
      {!staff.data ? (
        <Loading error={staff.error} what="the team" retry={() => void staff.reload()} />
      ) : staff.data.staff.length === 0 ? (
        <Empty>No one on the team yet.</Empty>
      ) : (
        <>
          <label className="inline-label">
            <span className="flabel">Person</span>
            <select className="field" value={staffId} onChange={(e) => setStaffId(e.target.value)}>
              <option value="">Pick someone…</option>
              {staff.data.staff.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.displayName}
                </option>
              ))}
            </select>
          </label>
          {person && (
            <>
              <p className="muted small">Saving replaces what’s set now for {person.displayName}.</p>
              <PayForm key={`pay-${person.id}`} person={person} toast={toast} />
              <PlanForm key={`plan-${person.id}`} person={person} toast={toast} />
            </>
          )}
        </>
      )}
    </Panel>
  );
}

function PayForm({ person, toast }: { person: Staff; toast: (m: string) => void }) {
  const [kind, setKind] = useState<'field' | 'office'>(person.isTechnician ? 'field' : 'office');
  const [payType, setPayType] = useState<'hourly' | 'salary'>('hourly');
  const [amount, setAmount] = useState('');
  const [providerId, setProviderId] = useState('');
  const [since, setSince] = useState('');
  const act = useAct(toast, () => undefined);
  const cents = parseMoney(amount);
  const ok = cents !== null && cents >= 0;
  return (
    <form
      className="fgrid inline-form"
      aria-label={`${person.displayName}’s pay`}
      onSubmit={(e) => {
        e.preventDefault();
        if (!ok || act.busy) return;
        void act.run(
          'payroll.setPay',
          {
            staffId: person.id,
            kind,
            payType,
            ...(payType === 'hourly' ? { hourlyRateCents: cents } : { salaryPeriodCents: cents }),
            ...(providerId.trim() ? { providerEmployeeId: providerId.trim() } : {}),
            ...(since ? { employedSince: since } : {}),
          },
          `${person.displayName}’s pay is saved.`,
        );
      }}
    >
      <h3 className="full h4">Pay</h3>
      <label>
        <span className="flabel">Works</span>
        <select className="field" value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
          <option value="field">In the field</option>
          <option value="office">In the office</option>
        </select>
      </label>
      <label>
        <span className="flabel">Paid</span>
        <select
          className="field"
          value={payType}
          onChange={(e) => setPayType(e.target.value as typeof payType)}
        >
          <option value="hourly">By the hour</option>
          <option value="salary">A salary</option>
        </select>
      </label>
      <label>
        <span className="flabel">{payType === 'hourly' ? 'Rate per hour' : 'Salary per pay period'}</span>
        <input
          className="field"
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="0.00"
        />
      </label>
      <label>
        <span className="flabel">Their id at the payroll provider</span>
        <input
          className="field"
          value={providerId}
          onChange={(e) => setProviderId(e.target.value)}
          placeholder="Optional"
        />
      </label>
      <label>
        <span className="flabel">First day (for stat and sick pay)</span>
        <input className="field" type="date" value={since} onChange={(e) => setSince(e.target.value)} />
      </label>
      <div className="row-actions">
        <button className="btn primary" type="submit" disabled={!ok || act.busy}>
          {act.busy ? 'Saving…' : 'Save the pay'}
        </button>
      </div>
    </form>
  );
}

function PlanForm({ person, toast }: { person: Staff; toast: (m: string) => void }) {
  const [rates, setRates] = useState<Record<string, string>>({});
  const act = useAct(toast, () => undefined);
  const parsed = COMMISSION_RULES.map(([k]) => [k, percentToRate(rates[k] ?? '')] as const);
  const ok = parsed.every(([, r]) => r !== null);
  return (
    <form
      className="fgrid inline-form"
      aria-label={`${person.displayName}’s commission`}
      onSubmit={(e) => {
        e.preventDefault();
        if (!ok || act.busy) return;
        void act.run(
          'commissions.setPlan',
          { staffId: person.id, ...Object.fromEntries(parsed) },
          `${person.displayName}’s commission rates are saved.`,
        );
      }}
    >
      <h3 className="full h4">Commission (% of the work)</h3>
      {COMMISSION_RULES.map(([k, label]) => (
        <label key={k}>
          <span className="flabel">{label}</span>
          <input
            className="field qty-in"
            inputMode="decimal"
            value={rates[k] ?? ''}
            onChange={(e) => setRates({ ...rates, [k]: e.target.value })}
            placeholder="0"
            aria-invalid={percentToRate(rates[k] ?? '') === null || undefined}
          />
        </label>
      ))}
      <div className="row-actions">
        <button className="btn primary" type="submit" disabled={!ok || act.busy}>
          {act.busy ? 'Saving…' : 'Save the commission rates'}
        </button>
      </div>
    </form>
  );
}

// ── a period's commissions ──

export function PeriodCommissions({
  periodId,
  open,
  toast,
  version,
  onChanged,
}: {
  periodId: string;
  open: boolean;
  toast: (m: string) => void;
  version: number;
  onChanged: () => void;
}) {
  const list = useQuery<{
    lines: { staffId: string; amountCents?: number }[];
    totals: { count: number; amountCents: number };
  }>('commissions.list', { periodId }, [version]);
  const staff = useQuery<{ staff: Staff[] }>('staff.list', {});
  const act = useAct(toast, async () => {
    await list.reload();
    onChanged();
  });
  const names = new Map((staff.data?.staff ?? []).map((s) => [s.id, s.displayName]));
  const rows = list.data ? commissionsByPerson(list.data.lines, names) : [];
  return (
    <div className="period-commissions">
      <div className="line-top">
        <div>
          <strong>Commissions</strong>
          <span className="muted small">
            {list.data
              ? list.data.totals.count
                ? `${list.data.totals.count} in this period`
                : 'None locked into this period yet.'
              : ''}
          </span>
        </div>
        {list.data && list.data.totals.count > 0 && (
          <Money cents={list.data.totals.amountCents} className="amount" />
        )}
      </div>
      {rows.length > 0 && rows.some((r) => r.amountCents !== null) && (
        <ul className="rows">
          {rows.map((r) => (
            <li key={r.staffId} className="inv-row">
              <div className="inv-main">
                <strong>{r.name}</strong>
                <span className="muted small">
                  {r.count} job{r.count === 1 ? '' : 's'}
                </span>
              </div>
              {r.amountCents !== null && <Money cents={r.amountCents} className="amount" />}
            </li>
          ))}
        </ul>
      )}
      {open && (
        <div className="row-actions">
          <button
            className="btn"
            disabled={act.busy}
            onClick={() =>
              void act
                .run<{ locked: number }>('commissions.lock', { periodId })
                .then(
                  (r) =>
                    r &&
                    toast(
                      r.locked
                        ? `${r.locked} commission${r.locked === 1 ? '' : 's'} locked into this period.`
                        : 'Nothing new to lock: every completed job’s commission is already in.',
                    ),
                )
            }
          >
            Lock commissions
          </button>
        </div>
      )}
    </div>
  );
}

// ── what the provider took from the bank ──

export function RecordFunding({
  period,
  timeZone,
  toast,
  onDone,
}: {
  period: PayPeriodSummary;
  timeZone?: string;
  toast: (m: string) => void;
  onDone: () => void;
}) {
  const banks = useQuery<{ bankAccounts: BankAccount[] }>('bankAccounts.list', {});
  const accounts = useQuery<{
    accounts: { id: string; code: string; name: string; type: string; active: boolean }[];
  }>('accounts.list', { includeInactive: false });
  const [sheet, setSheet] = useState(false);
  // Who sends CRA the deductions and employer costs: the provider (in the same pull) or the company.
  const [remitter, setRemitter] = useState<'provider' | 'company'>('provider');
  const [amount, setAmount] = useState('');
  const [fee, setFee] = useState('');
  const [feeAccountId, setFeeAccountId] = useState('');
  const [fundedOn, setFundedOn] = useState(todayIn(timeZone));
  const [bankId, setBankId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cents = parseMoney(amount);
  const feeCents = fee.trim() ? parseMoney(fee) : 0;
  const expenses = (accounts.data?.accounts ?? []).filter((a) => a.type === 'expense' && a.active);
  const feeAccount = expenses.find((a) => a.id === feeAccountId) ?? null;
  const bank = banks.data?.bankAccounts.find((b) => b.id === (bankId || banks.data?.bankAccounts[0]?.id));
  const ready =
    cents !== null &&
    cents > 0 &&
    !!fundedOn &&
    !!bank &&
    feeCents !== null &&
    feeCents >= 0 &&
    feeCents < cents &&
    (feeCents === 0 || !!feeAccount);
  if (!sheet)
    return (
      <button className="btn primary" onClick={() => setSheet(true)}>
        Record the funding
      </button>
    );
  const what =
    remitter === 'provider'
      ? 'net pay, and the deductions and employer costs it sends CRA'
      : 'net pay only; the deductions and employer costs are the company’s to send CRA';
  return (
    <MoneySheet
      title="Record the payroll funding"
      outcome={
        <>
          The provider took {cad(cents ?? 0)} from {bank?.name ?? 'the bank'} on {dayText(fundedOn)} for{' '}
          {dayText(period.startDate)} to {dayText(period.endDate)}: {what}
          {feeCents ? `, and its fee of ${cad(feeCents)}` : ''}.{' '}
          {remitter === 'provider'
            ? 'The payroll liabilities are cleared.'
            : 'The deductions and employer costs stay owing until the CRA payment is recorded.'}
        </>
      }
      confirm={`Record ${cad(cents ?? 0)}`}
      ready={ready}
      busy={busy}
      error={error}
      onConfirm={() => {
        if (!bank || cents === null || feeCents === null) return;
        setBusy(true);
        setError(null);
        // straight to the server, so a refusal (it names the amounts it expects) shows in the sheet
        client
          .call<{ remittedBy: 'provider' | 'company'; toRemitCents: number }>('payroll.recordFunding', {
            periodId: period.id,
            fundedOn,
            amountCents: cents,
            bankAccountId: bank.id,
            includesRemittances: remitter === 'provider',
            ...(feeCents && feeAccount ? { fee: { cents: feeCents, accountId: feeAccount.id } } : {}),
          })
          .then((r) => {
            setSheet(false);
            toast(
              r.remittedBy === 'company' && r.toRemitCents > 0
                ? `The funding is recorded. ${cad(r.toRemitCents)} is still to send CRA.`
                : 'The funding is recorded.',
            );
            onDone();
          })
          .catch((e: unknown) => setError(errorText(e)))
          .finally(() => setBusy(false));
      }}
      onClose={() => setSheet(false)}
    >
      <fieldset className="who-remits">
        <legend>Who sends CRA the deductions?</legend>
        <label className="checkrow">
          <input
            type="radio"
            name="remitter"
            checked={remitter === 'provider'}
            onChange={() => setRemitter('provider')}
          />{' '}
          The provider, in the same pull
        </label>
        <label className="checkrow">
          <input
            type="radio"
            name="remitter"
            checked={remitter === 'company'}
            onChange={() => setRemitter('company')}
          />{' '}
          We do: the provider took net pay only
        </label>
      </fieldset>
      <label>
        What the provider took
        <span className="muted small hint">The total on the bank statement, with any fee.</span>
        <input
          className="field"
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="0.00"
        />
      </label>
      <label>
        Its fee
        <span className="muted small hint">Optional: the part of it that was the provider’s fee.</span>
        <input
          className="field"
          inputMode="decimal"
          value={fee}
          onChange={(e) => setFee(e.target.value)}
          placeholder="0.00"
        />
      </label>
      {!!feeCents && (
        <label>
          Fee goes to
          <select className="field" value={feeAccountId} onChange={(e) => setFeeAccountId(e.target.value)}>
            <option value="">Pick an expense account…</option>
            {expenses.map((a) => (
              <option key={a.id} value={a.id}>
                {a.code} {a.name}
              </option>
            ))}
          </select>
        </label>
      )}
      <label>
        Taken on
        <input className="field" type="date" value={fundedOn} onChange={(e) => setFundedOn(e.target.value)} />
      </label>
      {(banks.data?.bankAccounts.length ?? 0) > 1 && (
        <label>
          From
          <select className="field" value={bank?.id ?? ''} onChange={(e) => setBankId(e.target.value)}>
            {banks.data!.bankAccounts.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </label>
      )}
    </MoneySheet>
  );
}

// ── what the company still owes CRA (runs it remits itself) ──

interface ToRemit {
  runs: { periodId: string; startDate: string; endDate: string; payDate: string; owingCents: number }[];
  owingCents: number;
}

/**
 * The deductions and employer costs of runs the company remits itself (payroll.toRemit), oldest
 * first, and "Record a CRA payment" (payroll.recordRemittance): one payment can cover several runs,
 * and a part payment clears the oldest first. Hidden when nothing is owed.
 */
export function StillToRemit({
  timeZone,
  toast,
  version,
}: {
  timeZone?: string;
  toast: (m: string) => void;
  version: number;
}) {
  const owed = useQuery<ToRemit>('payroll.toRemit', {}, [version]);
  const banks = useQuery<{ bankAccounts: BankAccount[] }>('bankAccounts.list', {});
  const [sheet, setSheet] = useState(false);
  const [amount, setAmount] = useState('');
  const [paidOn, setPaidOn] = useState(todayIn(timeZone));
  const [paidTo, setPaidTo] = useState<'cra' | 'provider'>('cra');
  const [reference, setReference] = useState('');
  const [bankId, setBankId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // held while the sheet is the same, so a retry after a lost answer records the payment once
  const save = useSaveKey(JSON.stringify([amount, paidOn, paidTo, reference, bankId]));
  if (!owed.data || owed.data.owingCents <= 0) return null;
  const total = owed.data.owingCents;
  const cents = amount.trim() ? parseMoney(amount) : total;
  const bank = banks.data?.bankAccounts.find((b) => b.id === (bankId || banks.data?.bankAccounts[0]?.id));
  const ready = cents !== null && cents > 0 && cents <= total && !!paidOn && !!bank;
  return (
    <Panel title="Still to send CRA" aside={<Money cents={total} className="amount" />}>
      <ul className="rows">
        {owed.data.runs.map((r) => (
          <li key={r.periodId} className="inv-row">
            <div className="inv-main">
              <strong>
                {dayText(r.startDate)} – {dayText(r.endDate)}
              </strong>
              <span className="muted small">Paid {dayText(r.payDate)} · deductions and employer costs</span>
            </div>
            <Money cents={r.owingCents} className="amount" />
          </li>
        ))}
      </ul>
      <div className="row-actions">
        <button className="btn primary" onClick={() => setSheet(true)}>
          Record a CRA payment
        </button>
      </div>
      {sheet && (
        <MoneySheet
          title="Record the CRA payment"
          outcome={
            <>
              {cad(cents ?? 0)} left {bank?.name ?? 'the bank'} on {dayText(paidOn)} for the payroll
              deductions and employer costs{paidTo === 'provider' ? ', through the provider' : ' to CRA'}. It
              pays the oldest run first
              {cents !== null && cents < total ? `; ${cad(total - cents)} stays owing` : ''}.
            </>
          }
          confirm={`Record ${cad(cents ?? 0)}`}
          ready={ready}
          busy={busy}
          error={error}
          onConfirm={() => {
            if (!bank || cents === null) return;
            setBusy(true);
            setError(null);
            client
              .call<{ stillToRemitCents: number }>('payroll.recordRemittance', {
                remittanceId: save.key,
                periodIds: owed.data!.runs.map((r) => r.periodId),
                amountCents: cents,
                paidOn,
                bankAccountId: bank.id,
                paidTo,
                ...(reference.trim() ? { reference: reference.trim() } : {}),
              })
              .then((r) => {
                setSheet(false);
                setAmount('');
                setReference('');
                save.renew();
                toast(
                  r.stillToRemitCents > 0
                    ? `Recorded. ${cad(r.stillToRemitCents)} is still to send CRA.`
                    : 'Recorded: nothing is owed to CRA for payroll.',
                );
                void owed.reload();
              })
              .catch((e: unknown) => setError(errorText(e)))
              .finally(() => setBusy(false));
          }}
          onClose={() => setSheet(false)}
        >
          <label>
            Amount
            <span className="muted small hint">Up to {cad(total)}.</span>
            <input
              className="field"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={(total / 100).toFixed(2)}
            />
          </label>
          <label>
            Paid on
            <input className="field" type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
          </label>
          <label>
            Paid
            <select
              className="field"
              value={paidTo}
              onChange={(e) => setPaidTo(e.target.value as typeof paidTo)}
            >
              <option value="cra">To CRA</option>
              <option value="provider">Through the payroll provider</option>
            </select>
          </label>
          <label>
            Reference
            <input
              className="field"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Optional: the CRA confirmation"
            />
          </label>
          {(banks.data?.bankAccounts.length ?? 0) > 1 && (
            <label>
              From
              <select className="field" value={bank?.id ?? ''} onChange={(e) => setBankId(e.target.value)}>
                {banks.data!.bankAccounts.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </label>
          )}
        </MoneySheet>
      )}
    </Panel>
  );
}

// ── the provider's pay register, entered by hand (a company without a payroll integration) ──

interface RegisterRow {
  gross: string;
  deductions: string;
  employer: string;
}

/**
 * After the run, the provider's register (gross, deductions, net, employer costs per person) is typed
 * in and posted (payroll.postRegister), so the period posts and its funding can be recorded. Each
 * person's gross must match the period's; net is worked out as gross less deductions, as the
 * register shows it. Every mismatch comes back from the server in one sentence.
 */
export function EnterRegister({
  period,
  toast,
  onDone,
}: {
  period: PayPeriod;
  toast: (m: string) => void;
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [reference, setReference] = useState('');
  const [rows, setRows] = useState<Record<string, RegisterRow>>(() =>
    Object.fromEntries(
      period.lines.map((l) => [
        l.staffId,
        {
          gross: l.grossCents !== undefined ? (l.grossCents / 100).toFixed(2) : '',
          deductions: '',
          employer: '',
        },
      ]),
    ),
  );
  const [sheet, setSheet] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (period.lines.length === 0) return null;
  if (!open)
    return (
      <button className="btn" onClick={() => setOpen(true)}>
        Enter the provider’s register
      </button>
    );
  const cents = (t: string) => (t.trim() ? parseMoney(t) : 0);
  const parsed = period.lines.map((l) => {
    const r = rows[l.staffId]!;
    const gross = r.gross.trim() ? parseMoney(r.gross) : null;
    const deductions = cents(r.deductions);
    const employer = cents(r.employer);
    const net = gross !== null && deductions !== null ? gross - deductions : null;
    return { line: l, gross, deductions, employer, net };
  });
  const ok =
    !!reference.trim() &&
    parsed.every(
      (p) =>
        p.gross !== null &&
        p.gross >= 0 &&
        p.deductions !== null &&
        p.deductions >= 0 &&
        p.employer !== null &&
        p.employer >= 0 &&
        p.net !== null &&
        p.net >= 0,
    );
  const sum = (k: 'gross' | 'deductions' | 'employer' | 'net') => parsed.reduce((t, p) => t + (p[k] ?? 0), 0);
  const set = (staffId: string, patch: Partial<RegisterRow>) =>
    setRows({ ...rows, [staffId]: { ...rows[staffId]!, ...patch } });
  const post = () => {
    setBusy(true);
    setError(null);
    client
      .call('payroll.postRegister', {
        periodId: period.id,
        reference: reference.trim(),
        lines: parsed.map((p) => ({
          staffId: p.line.staffId,
          grossCents: p.gross!,
          deductionsCents: p.deductions!,
          netCents: p.net!,
          employerCostsCents: p.employer!,
        })),
      })
      .then(() => {
        setSheet(false);
        setOpen(false);
        toast('The register is posted: the period is posted.');
        onDone();
      })
      .catch((e: unknown) => setError(errorText(e)))
      .finally(() => setBusy(false));
  };
  return (
    <>
      <form
        className="register inline-form"
        aria-label="The provider’s register"
        onSubmit={(e) => {
          e.preventDefault();
          if (ok) setSheet(true);
        }}
      >
        <p className="muted small">
          Type each person’s line from the provider’s pay register. Gross must match this period; net is gross
          less deductions.
        </p>
        <label className="inline-label">
          <span className="flabel">Provider’s run number</span>
          <input className="field" value={reference} onChange={(e) => setReference(e.target.value)} />
        </label>
        <div className="table-wrap" ref={scrollFocus}>
          <table className="grid-table" aria-label="Pay register">
            <thead>
              <tr>
                <th>Person</th>
                <th className="num">Gross</th>
                <th className="num">Deductions</th>
                <th className="num">Net</th>
                <th className="num">Employer costs</th>
              </tr>
            </thead>
            <tbody>
              {parsed.map((p) => (
                <tr key={p.line.staffId}>
                  <td>{p.line.name}</td>
                  <td className="num">
                    <input
                      className="field qty-in"
                      inputMode="decimal"
                      aria-label={`${p.line.name}’s gross`}
                      value={rows[p.line.staffId]!.gross}
                      onChange={(e) => set(p.line.staffId, { gross: e.target.value })}
                    />
                  </td>
                  <td className="num">
                    <input
                      className="field qty-in"
                      inputMode="decimal"
                      aria-label={`${p.line.name}’s deductions`}
                      value={rows[p.line.staffId]!.deductions}
                      onChange={(e) => set(p.line.staffId, { deductions: e.target.value })}
                      placeholder="0.00"
                    />
                  </td>
                  <td className="num">{p.net !== null ? <Money cents={p.net} /> : '—'}</td>
                  <td className="num">
                    <input
                      className="field qty-in"
                      inputMode="decimal"
                      aria-label={`${p.line.name}’s employer costs`}
                      value={rows[p.line.staffId]!.employer}
                      onChange={(e) => set(p.line.staffId, { employer: e.target.value })}
                      placeholder="0.00"
                    />
                  </td>
                </tr>
              ))}
              <tr className="total">
                <td>Total</td>
                <td className="num">
                  <Money cents={sum('gross')} />
                </td>
                <td className="num">
                  <Money cents={sum('deductions')} />
                </td>
                <td className="num">
                  <Money cents={sum('net')} />
                </td>
                <td className="num">
                  <Money cents={sum('employer')} />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="row-actions">
          <button className="btn primary" type="submit" disabled={!ok}>
            Post the register
          </button>
          <button className="btn quiet" type="button" onClick={() => setOpen(false)}>
            Cancel
          </button>
        </div>
      </form>
      {sheet && (
        <MoneySheet
          title="Post the pay register"
          outcome={
            <>
              Wages of {cad(sum('gross'))} and employer costs of {cad(sum('employer'))} post for{' '}
              {dayText(period.startDate)} to {dayText(period.endDate)}, against payroll liabilities; net pay
              of {cad(sum('net'))} is what the provider pays out. The period’s commissions are marked paid.
            </>
          }
          confirm={`Post ${cad(sum('gross'))}`}
          ready={ok}
          busy={busy}
          error={error}
          onConfirm={post}
          onClose={() => setSheet(false)}
        />
      )}
    </>
  );
}
